import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Popover,
} from 'react-aria-components';
import { parseDate, getLocalTimeZone, DateFormatter, today } from '@internationalized/date';
import { I18nProvider } from 'react-aria';
// import './DatePicker.css';
import "./DatePickerRater.css";

// Add this error handler at the top of your file or in a separate utility file
const handleResizeObserverError = (error) => {
  if (error.message.includes('ResizeObserver loop completed with undelivered notifications')) {
    // Ignore this specific error as it's generally harmless
    return;
  }
  console.error('ResizeObserver error:', error);
};

// Add error listener
if (typeof window !== 'undefined') {
  window.addEventListener('error', handleResizeObserverError);
}

const DatePickerField = ({
  fieldName,
  formData,
  handleDateChange,
  handleBlur,
  touchedFields,
  fieldErrors,
}) => {
  const parseInitialDate = useCallback((dateString) => {
    if (!dateString || typeof dateString !== 'string') return null;
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const [mm, dd, yyyy] = parts;
      if (yyyy.length === 4) {
        try {
          return parseDate(`${yyyy}-${mm}-${dd}`);
        } catch {
          return null;
        }
      }
    }
    return null;
  }, []);

  const [dateValue, setDateValue] = useState(() => parseInitialDate(formData[fieldName]));
  const [mode, setMode] = useState('day');
  const [visibleYear, setVisibleYear] = useState(dateValue?.year || today(getLocalTimeZone()).year);
  const [visibleMonth, setVisibleMonth] = useState(dateValue?.month || today(getLocalTimeZone()).month);

  // Debounce state updates to prevent rapid re-renders
  const [updateTimeout, setUpdateTimeout] = useState(null);

  useEffect(() => {
    const parsed = parseInitialDate(formData[fieldName]);
    if (parsed?.toString() !== dateValue?.toString()) {
      // Clear any existing timeout
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }

      // Debounce the update
      const timeout = setTimeout(() => {
        setDateValue(parsed || null);
        if (parsed) {
          setVisibleYear(parsed.year);
          setVisibleMonth(parsed.month);
        }
      }, 10);

      setUpdateTimeout(timeout);
    }

    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
    };
  }, [formData[fieldName], parseInitialDate]);

  const formatDateForForm = useCallback((dateObj) => {
    const formatter = new DateFormatter('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      calendar: 'gregory',
    });
    return formatter.format(dateObj.toDate(getLocalTimeZone())).replace(/\//g, '-');
  }, []);

  const isInvalidFormat = useCallback((val) => {
    const parts = val?.split('-') || [];
    const [mm, dd, yyyy] = parts;
    return parts.length !== 3 || mm.length !== 2 || dd.length !== 2 || yyyy.length !== 4;
  }, []);

  // const handleConfirm = useCallback(() => {
  //   if (!dateValue) {
  //     handleBlur(fieldName, 'MM/DD/YYYY is required');
  //     return;
  //   }
  //   const formatted = formatDateForForm(dateValue);
  //   handleDateChange(fieldName, formatted);
  //   handleBlur(fieldName, isInvalidFormat(formatted) ? 'MM/DD/YYYY is required' : '');
  // }, [dateValue, fieldName, formatDateForForm, isInvalidFormat, handleBlur, handleDateChange]);

  const isDateInRange = useCallback((date) => {
    const min = parseDate('1926-01-01');
    const max = parseDate('2026-12-31');
    return date.compare(min) >= 0 && date.compare(max) <= 0;
  }, []);

 const getLastDayOfMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };
  const onDateChange = (newDate) => {
    if (!newDate) {
      setDateValue(null);
      // FIX: Change parameter order to match GeneralTab's expectation
      handleDateChange(null, fieldName);
      return;
    }

    // Check if the date is valid and complete
    const year = newDate.year;
    const month = newDate.month;
    const day = newDate.day;

    // Only proceed if we have a reasonable year (4 digits)
    if (year < 1000 || year > 9999) {
      setDateValue(newDate);
      return;
    }

    let adjustedDate = newDate;

    const lastDayCurrentMonth = getLastDayOfMonth(year, month);

    if (day > lastDayCurrentMonth) {
      adjustedDate = newDate.set({ day: lastDayCurrentMonth });
    }

    setDateValue(adjustedDate);

    // Only format and send to parent if we have a complete, valid date
    try {
      const formatter = new DateFormatter('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        calendar: 'gregory'
      });
      const formattedAsMMDDYYYYSlash = adjustedDate ? formatter.format(adjustedDate.toDate(getLocalTimeZone())) : '';
      const formattedForFormData = formattedAsMMDDYYYYSlash.replace(/\//g, '-');

      // FIX: Change parameter order to match GeneralTab's expectation
      handleDateChange(formattedForFormData, fieldName);
    } catch (error) {
      console.error(`DatePickerField: Error formatting date:`, error);
      // Don't update parent if there's an error
    }
  };

  const handleDateSelect = useCallback((date) => {
    setDateValue(date);
    if (isDateInRange(date)) {
      const formatted = formatDateForForm(date);
      handleDateChange(fieldName, formatted);
      handleBlur(fieldName, '');
    } else {
      handleBlur(fieldName, 'Date is out of allowed range');
    }
  }, [isDateInRange, formatDateForForm, fieldName, handleDateChange, handleBlur]);

    // const renderYearGrid = useCallback(() => {
  //   const start = visibleYear - 12;
  //   return (
  //     <div className="year-grid">
  //       {Array.from({ length: 24 }, (_, i) => {
  //         const year = start + i;
  //         return (
  //           <div
  //             key={year}
  //             className={`year-cell ${year === visibleYear ? 'selected' : ''}`}
  //             onClick={() => {
  //               setVisibleYear(year);
  //               setMode('day');
  //             }}
  //           >
  //             {year}
  //           </div>
  //         );
  //       })}
  //     </div>
  //   );
  // }, [visibleYear]);

  const renderYearGrid = useCallback(() => {
    const start = Math.max(1926, visibleYear - 12);
    const years = [];

    for (let y = start; y <= Math.min(2026, start + 23); y++) {
      years.push(
        <div
          key={y}
          className={`year-cell ${y === visibleYear ? 'selected' : ''}`}
          onClick={() => {
            setVisibleYear(y);
            setMode('day');
          }}
        >
          {y}
        </div>
      );
    }

    return <div className="year-grid">{years}</div>;
  }, [visibleYear]);
  const renderMonthGrid = useCallback(() => {
    const months = Array.from({ length: 12 }, (_, i) =>
      new Date(0, i).toLocaleString('en-US', { month: 'short' })
    );

    return (
      <div className="month-grid">
        {months.map((monthName, index) => (
          <div
            key={index}
            className={`month-cell ${index + 1 === visibleMonth ? 'selected' : ''}`}
            onClick={() => {
              setVisibleMonth(index + 1);
              setMode('day');
            }}
          >
            {monthName}
          </div>
        ))}
      </div>
    );
  }, [visibleMonth]);

 // Memoize the navigation handlers to prevent unnecessary re-renders
  // const handlePrevNavigation = useCallback(() => {
  //   if (mode === 'year') {
  //     setVisibleYear(y => y - 12);
  //   } else if (mode === 'day') {
  //     if (visibleMonth === 1) {
  //       setVisibleMonth(12);
  //       setVisibleYear(y => y - 1);
  //     } else {
  //       setVisibleMonth(m => m - 1);
  //     }
  //   } else if (mode === 'month') {
  //     setVisibleYear(y => y - 1);
  //   }
  // }, [mode, visibleMonth]);
  const handlePrevNavigation = useCallback(() => {
      if (mode === 'year') {
        setVisibleYear(y => Math.max(1926, y - 12));
      } else if (mode === 'day') {
        if (visibleMonth === 1) {
          if (visibleYear > 1926) {
            setVisibleMonth(12);
            setVisibleYear(y => y - 1);
          }
        } else {
          setVisibleMonth(m => m - 1);
        }
      } else if (mode === 'month') {
        setVisibleYear(y => Math.max(1926, y - 1));
      }
    }, [mode, visibleMonth]);

  // const handleNextNavigation = useCallback(() => {
  //   if (mode === 'year') {
  //     setVisibleYear(y => y + 12);
  //   } else if (mode === 'day') {
  //     if (visibleMonth === 12) {
  //       setVisibleMonth(1);
  //       setVisibleYear(y => y + 1);
  //     } else {
  //       setVisibleMonth(m => m + 1);
  //     }
  //   } else if (mode === 'month') {
  //     setVisibleYear(y => y + 1);
  //   }
  // }, [mode, visibleMonth]);

  // Memoize the calendar title to prevent unnecessary re-renders
  const handleNextNavigation = useCallback(() => {
      if (mode === 'year') {
        setVisibleYear(y => Math.min(2026, y + 12));
      } else if (mode === 'day') {
        if (visibleMonth === 12) {
          if (visibleYear < 2026) {
            setVisibleMonth(1);
            setVisibleYear(y => y + 1);
          }
        } else {
          setVisibleMonth(m => m + 1);
        }
      } else if (mode === 'month') {
        setVisibleYear(y => Math.min(2026, y + 1));
      }
    }, [mode, visibleMonth]);

  // const calendarTitle = useMemo(() => {
  //   if (mode === 'year') {
  //     return `${visibleYear - 12} - ${visibleYear + 11}`;
  //   } else {
  //     return (
  //       <span>
  //         <span
  //           onClick={() => setMode('month')}
  //           style={{ cursor: 'pointer', marginRight: '4px', textDecoration: 'underline' }}
  //         >
  //           {new Date(visibleYear, visibleMonth - 1).toLocaleString('en-US', { month: 'long' })}
  //         </span>
  //         <span
  //           onClick={() => setMode('year')}
  //           style={{ cursor: 'pointer', textDecoration: 'underline' }}
  //         >
  //           {visibleYear}
  //         </span>
  //       </span>
  //     );
  //   }
  // }, [mode, visibleYear, visibleMonth]);

  // Memoize the focused calendar value
  const calendarTitle = useMemo(() => {
      if (mode === 'year') {
        return `${Math.max(1926, visibleYear - 12)} - ${Math.min(2026, visibleYear + 11)}`;
      } else {
        return (
          <span>
            <span
              onClick={() => setMode('month')}
              style={{ cursor: 'pointer', marginRight: '4px', textDecoration: 'underline' }}
            >
              {new Date(visibleYear, visibleMonth - 1).toLocaleString('en-US', { month: 'long' })}
            </span>
            <span
              onClick={() => setMode('year')}
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              {visibleYear}
            </span>
          </span>
        );
      }
    }, [mode, visibleYear, visibleMonth]);
  const focusedValue = useMemo(() => {
    return parseDate(`${visibleYear}-${String(visibleMonth).padStart(2, '0')}-01`);
  }, [visibleYear, visibleMonth]);

  return (
    <I18nProvider locale="en-US">
      <div className="date-picker-container">
         <DatePicker
          value={dateValue}
          onChange={onDateChange}
        onBlur={() => handleBlur(fieldName)}
        //  minValue={parseDate('1940-01-01')}
        //  maxValue={parseDate('2008-12-31')}
        minValue={fieldName === "dob" ? parseDate('1940-01-01') : parseDate('1940-01-01')}
  maxValue={fieldName === "dob" ? parseDate('2008-12-31') : parseDate('2040-12-31')}
        className="react-aria-DatePicker"
          granularity="day"
          shouldForceLeadingZeros={true}
          aria-label={fieldName === "dob" ? "Date of Birth" : "Select Date"}
        >
          <Group className={`react-aria-GroupRater ${touchedFields?.[fieldName] && fieldErrors?.[fieldName] ? 'error' : ''}`}>
            <DateInput className="react-aria-DateInput">
              {(segment) =>
                segment.type === 'literal' ? (
                  <span key={segment.key} className="separator">-</span>
                ) : (
                  <DateSegment
                    key={segment.key}
                    segment={segment}
                    className="react-aria-DateSegment"
                  />
                )
              }
            </DateInput>
            <Button className="react-aria-Button" aria-label="Open calendar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </Button>
          </Group>

          {touchedFields?.[fieldName] && fieldErrors?.[fieldName] && (
            <div style={{ color: 'red', fontSize: '13px', marginTop: '4px' }}>
              {fieldErrors[fieldName]}
            </div>
          )}

          <Popover className="react-aria-Popover">
            <Dialog className="react-aria-Dialog" aria-label="Calendar dialog">
              <div className="calendar-header">
                <Button onPress={handlePrevNavigation} aria-label="Previous period">◀</Button>
                <Heading className="calendar-title">
                  {calendarTitle}
                </Heading>
                <Button onPress={handleNextNavigation} aria-label="Next period">▶</Button>
              </div>

              {mode === 'year' && renderYearGrid()}
              {mode === 'month' && renderMonthGrid()}

              {mode === 'day' && (
                <Calendar focusedValue={focusedValue}>
                  <CalendarGrid className="react-aria-CalendarGrid">
                    {(date) => (
                      <CalendarCell
                        date={date}
                        className="react-aria-CalendarCell"
                        onPress={() => handleDateSelect(date)}
                      />
                    )}
                  </CalendarGrid>
                </Calendar>
              )}
            </Dialog>
          </Popover>
        </DatePicker>
      </div>
    </I18nProvider>
  );
};

export default DatePickerField;


// import {
//   Button,
//   Calendar,
//   CalendarCell,
//   CalendarGrid,
//   DateInput,
//   DatePicker,
//   DateSegment,
//   Dialog,
//   Group,
//   Heading,
//   Label,
//   Popover,
// } from 'react-aria-components';
// import { useState, useEffect } from 'react';
// import { parseDate, toCalendarDate, getLocalTimeZone, DateFormatter } from '@internationalized/date';
// import { I18nProvider } from 'react-aria';
// // import './DatePickerCommon.css';
// import './DatePicker.css';
// const DatePickerField = ({
//   fieldName,
//   formData,
//   handleDateChange,
//   handleBlur,
//   touchedFields,
//   fieldErrors,
// }) => {

//   // Safely parse initial date
//   const parseInitialDate = (dateString) => {
//     if (!dateString) {
//       return null;
//     }
//     const parts = dateString.split('-');
//     if (parts.length === 3) {
//       const year = parts[2];
//       const month = parts[0];
//       const day = parts[1];
//       const isoFormatted = `${year}-${month}-${day}`;
//       try {
//         return parseDate(isoFormatted);
//       } catch (e) {
//         console.error(`DatePickerField: Error parsing initial date '${dateString}' (converted to '${isoFormatted}') for ${fieldName}:`, e);
//         return null;
//       }
//     } else {
//       console.warn(`DatePickerField: Initial date string for ${fieldName} has unexpected format: ${dateString}. Expected MM-DD-YYYY.`);
//       return null;
//     }
//   };

//   const [dateValue, setDateValue] = useState(() => parseInitialDate(formData[fieldName]));

//   const getLastDayOfMonth = (year, month) => {
//     return new Date(year, month, 0).getDate();
//   };

  // const onDateChange = (newDate) => {
  //   if (!newDate) {
  //     setDateValue(null);
  //     // FIX: Change parameter order to match GeneralTab's expectation
  //     handleDateChange(null, fieldName);
  //     return;
  //   }

  //   // Check if the date is valid and complete
  //   const year = newDate.year;
  //   const month = newDate.month;
  //   const day = newDate.day;

  //   // Only proceed if we have a reasonable year (4 digits)
  //   if (year < 1000 || year > 9999) {
  //     setDateValue(newDate);
  //     return;
  //   }

  //   let adjustedDate = newDate;

  //   const lastDayCurrentMonth = getLastDayOfMonth(year, month);

  //   if (day > lastDayCurrentMonth) {
  //     adjustedDate = newDate.set({ day: lastDayCurrentMonth });
  //   }

  //   setDateValue(adjustedDate);

  //   // Only format and send to parent if we have a complete, valid date
  //   try {
  //     const formatter = new DateFormatter('en-US', {
  //       year: 'numeric',
  //       month: '2-digit',
  //       day: '2-digit',
  //       calendar: 'gregory'
  //     });
  //     const formattedAsMMDDYYYYSlash = adjustedDate ? formatter.format(adjustedDate.toDate(getLocalTimeZone())) : '';
  //     const formattedForFormData = formattedAsMMDDYYYYSlash.replace(/\//g, '-');

  //     // FIX: Change parameter order to match GeneralTab's expectation
  //     handleDateChange(formattedForFormData, fieldName);
  //   } catch (error) {
  //     console.error(`DatePickerField: Error formatting date:`, error);
  //     // Don't update parent if there's an error
  //   }
  // };

//   // Helper function to safely compare dates
//   const datesAreEqual = (date1, date2) => {
//     if (date1 === null && date2 === null) return true;
//     if (date1 === null || date2 === null) return false;

//     // Check if both objects have the equals method
//     if (typeof date1.equals === 'function' && typeof date2.equals === 'function') {
//       return date1.equals(date2);
//     }

//     // Fallback comparison using toString or direct comparison
//     if (date1.toString && date2.toString) {
//       return date1.toString() === date2.toString();
//     }

//     // Last resort: direct comparison
//     return date1 === date2;
//   };

//   // --- Crucial Change: Revised useEffect logic ---
//   useEffect(() => {
//     const newParsedDate = parseInitialDate(formData[fieldName]);

//     // Scenario 1: Both current state and new parsed date are null
//     if (newParsedDate === null && dateValue === null) {
//       return; // No change needed
//     }

//     // Scenario 2: newParsedDate is null, but dateValue is not (means formData cleared the date)
//     if (newParsedDate === null && dateValue !== null) {
//       setDateValue(null);
//       return;
//     }

//     // Scenario 3: newParsedDate is NOT null, but dateValue IS null (means formData received a date)
//     if (newParsedDate !== null && dateValue === null) {
//       setDateValue(newParsedDate);
//       return;
//     }

//     // Scenario 4: Both newParsedDate and dateValue are NOT null. Now we can safely compare using our helper function
//     if (newParsedDate !== null && dateValue !== null && !datesAreEqual(newParsedDate, dateValue)) {
//       setDateValue(newParsedDate);
//       return;
//     }

//     // If none of the above conditions met, it means newParsedDate and dateValue are
//     // both non-null and equal, so no update is needed.

//   }, [formData[fieldName], fieldName]); // Keep dateValue OUT of the dependency array for this pattern.

//   return (
//     <I18nProvider locale="en-US">
//         <div className="date-picker-container">
//       <DatePicker
//        value={dateValue}
//         onChange={onDateChange}
//         onBlur={() => handleBlur(fieldName)}
//         //  minValue={parseDate('1940-01-01')}
//         //  maxValue={parseDate('2008-12-31')}
//         minValue={fieldName === "dob" ? parseDate('1940-01-01') : parseDate('1940-01-01')}
//   maxValue={fieldName === "dob" ? parseDate('2008-12-31') : parseDate('2040-12-31')}
//         className="react-aria-DatePicker"
//         granularity="day"
//         shouldForceLeadingZeros={true}
//       >
//         <Group
//           className={`react-aria-Group ${touchedFields[fieldName] && fieldErrors[fieldName] ? 'error' : ''}`}
//         >
//           <DateInput className="react-aria-DateInput">
//             {(segment) => <DateSegment
//              segment={segment}
//               className="react-aria-DateSegment"
//               style={{
//                 minWidth: segment.type === 'month' || segment.type === 'day' ? '2ch' :
//                          segment.type === 'year' ? '4ch' : 'auto',
//                 textAlign: 'center'
//               }}
//             />}
//           </DateInput>
//            <Button className="react-aria-Button">
//             <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
//               <path d="M7 10l5 5 5-5z"/>
//             </svg>
//           </Button>
//         </Group>
//          {touchedFields[fieldName] && fieldErrors[fieldName] && (
//           <div className="error-message">{fieldErrors[fieldName]}</div>
//         )}
//         <Popover className="react-aria-Popover">
//           <Dialog className="react-aria-Dialog">
//             <Calendar className="react-aria-Calendar">
//               <header className="calendar-header">
//                 <Button slot="previous">◀</Button>
//                 <Heading />
//                 <Button slot="next">▶</Button>
//               </header>
//               <CalendarGrid className="react-aria-CalendarGrid">
//                 {(date) => <CalendarCell date={date} className="react-aria-CalendarCell" />}
//               </CalendarGrid>
//             </Calendar>
//           </Dialog>
//         </Popover>
//       </DatePicker>
//       </div>
//     </I18nProvider>
//   );
// };

// export default DatePickerField;

// import {
//   Button,
//   Calendar,
//   CalendarCell,
//   CalendarGrid,
//   DateInput,
//   DatePicker,
//   DateSegment,
//   Dialog,
//   Group,
//   Heading,
//   Label,
//   Popover,
// } from 'react-aria-components';
// import { useState, useEffect } from 'react';
// import { parseDate,DateFormatter,getLocalTimeZone } from '@internationalized/date';
// import { I18nProvider } from 'react-aria';
// import './DatePicker.css';

// const DatePickerField = ({
//   fieldName,
//   formData,
//   handleDateChange,
//   handleBlur,
//   touchedFields,
//   fieldErrors,
// }) => {
//   // Parse initial date from MM-DD-YYYY format (your form data format)
//   const parseInitialDate = (dateStr) => {
//     if (!dateStr) return null;
//     try {
//       // Parse MM-DD-YYYY format (your form data format)
//       const [month, day, year] = dateStr.split('-');
//       if (!day || !month || !year) return null;
//       return parseDate(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
//     } catch (error) {
//       return null;
//     }
//   };

//   const [date, setDate] = useState(null);

//   // Update date when formData changes
//   useEffect(() => {
//     const parsedDate = parseInitialDate(formData[fieldName]);
//     setDate(parsedDate);
//   }, [formData[fieldName]]);

//   // Handle date changes and output in MM-DD-YYYY format (to match your handleDateChange function)
//   // const onDateChange = (newDate) => {
//   //   setDate(newDate);
//   //   if (newDate) {
//   //     // Format as MM-DD-YYYY to match your handleDateChange function
//   //     const formattedDate = `${newDate.month.toString().padStart(2, '0')}-${newDate.day
//   //       .toString()
//   //       .padStart(2, '0')}-${newDate.year}`;
//   //     handleDateChange(formattedDate, fieldName);
//   //   } else {
//   //     handleDateChange(null, fieldName);
//   //   }
//   // };
// const getLastDayOfMonth = (year, month) => {
//     return new Date(year, month, 0).getDate();
//   };

//    const onDateChange = (newDate) => {
//       if (!newDate) {
//         setDate(null);
//         handleDateChange(null, fieldName);
//         return;
//       }

//       let adjustedDate = newDate;

//       const year = newDate.year;
//       const month = newDate.month;
//       const day = newDate.day;

//       const lastDayCurrentMonth = getLastDayOfMonth(year, month);

//       if (day > lastDayCurrentMonth) {
//         console.log(`DatePickerField: Correcting day from ${day} to ${lastDayCurrentMonth} for month ${month}/${year}`);
//         adjustedDate = newDate.set({ day: lastDayCurrentMonth });
//       }

//       setDate(adjustedDate);

//       const formatter = new DateFormatter('en-US', {
//         year: 'numeric',
//         month: '2-digit',
//         day: '2-digit',
//         calendar: 'gregory'
//       });
//       const formattedAsMMDDYYYYSlash = adjustedDate ? formatter.format(adjustedDate.toDate(getLocalTimeZone())) : '';
//       const formattedForFormData = formattedAsMMDDYYYYSlash.replace(/\//g, '-');

//       console.log(`DatePickerField: New date value set to: ${adjustedDate?.toString()}, formatted for parent: ${formattedForFormData}`);

//       handleDateChange( formattedForFormData,fieldName);
//     };
//   return (
//     <I18nProvider locale="en-US">
//       <div className="date-picker-container">
//         <DatePicker
//           value={date}
//           onChange={onDateChange}
//           minValue={parseDate('1940-01-01')}
//           maxValue={parseDate('2008-12-31')}
//           // isDateUnavailable={isDateUnavailable}
//           isRequired
//           onBlur={() => handleBlur(fieldName)}
//           className="react-aria-DatePicker"
//           granularity="day"
//         >
//         {/* <Label className="react-aria-Label">Date of Birth</Label> */}
//         <Group
//           className={`react-aria-Group ${touchedFields[fieldName] && fieldErrors[fieldName] ? 'error' : ''}`}
//         >
//           <DateInput className="react-aria-DateInput">
//             {(segment) => (
//               <DateSegment
//                 segment={segment}
//                 placeholder="MM/DD/YYYY"
//                 className="react-aria-DateSegment"
//               />
//             )}
//           </DateInput>
//           <Button className="react-aria-Button">
//             <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
//               <path d="M7 10l5 5 5-5z"/>
//             </svg>
//           </Button>
//         </Group>
//         {/* {touchedFields[fieldName] && fieldErrors[fieldName] && (
//           <div className="error-message">{fieldErrors[fieldName]}</div>
//         )} */}
//         <Popover className="react-aria-Popover">
//           <Dialog className="react-aria-Dialog">
//             <Calendar className="react-aria-Calendar">
//               <header className="calendar-header">
//                 <Button slot="previous" className="nav-button">
//                   <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
//                     <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
//                   </svg>
//                 </Button>
//                 <Heading className="react-aria-Heading" />
//                 <Button slot="next" className="nav-button">
//                   <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
//                     <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
//                   </svg>
//                 </Button>
//               </header>
//               <CalendarGrid className="react-aria-CalendarGrid">
//                 {(date) => <CalendarCell date={date} className="react-aria-CalendarCell" />}
//               </CalendarGrid>
//             </Calendar>
//           </Dialog>
//         </Popover>
//       </DatePicker>
//     </div>
//     </I18nProvider>
//   );
// };

// export default DatePickerField;